import { Box, Button, Grid, TextField, Typography } from "@mui/material"
import { ChangeEvent, useState } from "react"
import COLORS from "../../common/FortesColors"
import AddOutlinedIcon from "@mui/icons-material/AddOutlined"
import RemoveOutlinedIcon from "@mui/icons-material/RemoveOutlined"
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp"
import { UIWidth, widthToPx } from "../../common/Models"

export function FortesTextField(props: {
  label: string | undefined
  initalValue: string
  enabled: boolean
  onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
}): JSX.Element {
  return (
    <Box>
      {props.label && (
        <Typography
          paddingRight="20px"
          color={COLORS.darkPurple}
          fontSize={"80%"}
          fontWeight={400}
          maxWidth="200px"
          maxHeight="33px"
        >
          {props.label}
        </Typography>
      )}
      <FortesBaseField
        width="300px"
        initalValue={props.initalValue}
        enabled={props.enabled}
        onChange={props.onChange}
        numberType={false}
      />
    </Box>
  )
}

export function FortesNumberFieldAdvanced(props: {
  label: string
  initalValue: number
  enabled: boolean
  onChange: (new_: number) => void
}): JSX.Element {
  return (
    <Box>
      <Typography
        paddingRight="20px"
        color={COLORS.darkPurple}
        fontSize={"80%"}
        fontWeight={400}
        maxWidth="200px"
        maxHeight="33px"
      >
        {props.label}
      </Typography>
      <Grid container spacing={0.5}>
        <Grid item>
          <NumberButton
            sign="-"
            onClick={() => {
              if (props.initalValue > 0) props.onChange(props.initalValue - 1)
            }}
          />
        </Grid>
        <Grid item>
          <FortesNumberBaseField
            width="ExtraSmall"
            initalValue={props.initalValue}
            onChange={(
              e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            ) => props.onChange(Number(e.target.value))}
          />
        </Grid>
        <Grid item>
          <NumberButton
            sign="+"
            onClick={() => {
              props.onChange(props.initalValue + 1)
            }}
          />
        </Grid>
      </Grid>
    </Box>
  )
}

export function FortesNumberFieldBasic(props: {
  label: string
  initalValue: number
  enabled?: boolean
  width?: UIWidth
  onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
}): JSX.Element {
  return (
    <Box>
      <Typography
        paddingRight="20px"
        color={COLORS.darkPurple}
        fontSize={"80%"}
        fontWeight={400}
        maxHeight="33px"
      >
        {props.label}
      </Typography>
      <FortesNumberBaseField
        enabled={props.enabled}
        width={props.width}
        initalValue={props.initalValue}
        onChange={props.onChange}
      />
    </Box>
  )
}

export function FortesDropdown<T>(props: {
  label: string
  width: UIWidth
  options: Array<T>
  initialValue: T
  onChange: (newVal: T) => void
  stringToT: (toConvert: string) => T | undefined
  TToString: (toConvert: T) => string
}): JSX.Element {
  const width = widthToPx(props.width)

  return (
    <Box>
      <Typography
        paddingRight="20px"
        color={COLORS.darkPurple}
        fontSize={"80%"}
        fontWeight={400}
        maxHeight="33px"
      >
        {props.label}
      </Typography>

      <CustomSelect
        initialValue={props.TToString(props.initialValue)}
        options={props.options.map((val: T) => props.TToString(val))}
        width={width}
        onChange={(newVal: string) => {
          const converted = props.stringToT(newVal)
          if (converted) props.onChange(converted)
        }}
      />
    </Box>
  )
}

function NumberButton(props: {
  onClick: () => void
  sign: "+" | "-"
}): JSX.Element {
  return (
    <Button
      sx={{
        height: "40px",
        minWidth: "1px",
        width: "40px",
        color: COLORS.darkPurple,
        backgroundColor: "white",
        border: `1px solid ${COLORS.separator}`,
        borderRadius: "14px",
      }}
      onClick={props.onClick}
    >
      {props.sign === "+" ? <AddOutlinedIcon /> : <RemoveOutlinedIcon />}
    </Button>
  )
}

function FortesNumberBaseField(props: {
  initalValue: number
  enabled?: boolean
  width?: UIWidth
  onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
}): JSX.Element {
  const width = widthToPx(props.width || "Small")

  return (
    <FortesBaseField
      width={width}
      initalValue={`${props.initalValue}`}
      enabled={props.enabled === undefined ? true : props.enabled}
      onChange={props.onChange}
      numberType={true}
      sx={{
        "& input[type=number]::-webkit-outer-spin-button": {
          "-webkit-appearance": "none",
          margin: 0,
        },
        "& input[type=number]::-webkit-inner-spin-button": {
          "-webkit-appearance": "none",
          margin: 0,
        },
      }}
    />
  )
}

export function CustomSelect(props: {
  initialValue: string
  options: Array<string>
  width: string
  onChange: (newVal: string) => void
}): JSX.Element {
  const [open, setOpen] = useState(false)

  return (
    <>
      <Box
        sx={{
          height: "40px",
          width: props.width,
          backgroundColor: "white",
          border: !open ? "1px solid #dfe0f2" : "2px solid #1a0549",
          borderRadius: "14px",
          display: "flex",
          flexDirection: "row",
        }}
        onClick={() => {
          setOpen(!open)
        }}
      >
        <Box
          sx={{
            padding: "0px 0px 0px 10px",
            width: "70%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            color: COLORS.darkPurple,
          }}
        >
          {`${props.initialValue}`}
        </Box>
        <Box
          sx={{
            height: "100%",
            maxWidth: "30%",
            minWidth: "30%",
            color: COLORS.darkPurple,
            display: "flex",
            alignItems: "center",
          }}
        >
          {!open && <ArrowDropDownIcon />}
          {open && <ArrowDropUpIcon />}
        </Box>
      </Box>
      {open && (
        <Box
          sx={{
            height: (40 * props.options.length).toString() + "px",
            backgroundColor: "white",
            border: "1px solid #dfe0f2",
            borderRadius: "14px",
            color: COLORS.darkPurple,
          }}
        >
          {props.options.map((option) => (
            <Box
              sx={{
                height: "40px",
                display: "flex",
                alignItems: "center",
                padding: "0px 0px 0px 10px",
              }}
              onClick={() => {
                props.onChange(option)
                setOpen(false)
              }}
            >
              {option}
            </Box>
          ))}
        </Box>
      )}
    </>
  )
}

function FortesBaseField(props: {
  initalValue: string
  enabled: boolean
  width: string
  onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  numberType: boolean
  sx?: any
}): JSX.Element {
  const ownSx = props.sx

  return (
    <TextField
      disabled={!props.enabled}
      value={props.initalValue}
      type={props.numberType ? "number" : ""}
      onChange={props.onChange}
      InputProps={
        props.numberType
          ? {
              inputProps: { min: 0, max: 300 },
            }
          : {}
      }
      sx={{
        width: props.width,
        height: "40px",
        backgroundColor: "white",
        border: "1px solid #dfe0f2",
        borderRadius: "14px",
        "& .MuiOutlinedInput-notchedOutline": {
          border: "0px solid black",
        },
        "& .MuiOutlinedInput-root": {
          color: COLORS.darkPurple,
          padding: "0px",
          borderRadius: "14px",
          "&.Mui-focused fieldset": {
            borderColor: "#1a0549",
          },
        },
        "& .MuiOutlinedInput-input": {
          padding: "5px 10px 5px 10px",
        },
        input: {
          //Dont know why but 31px input and 40px textfield is the same height
          height: "31px",
        },
        ...ownSx,
      }}
    />
  )
}
